import { 
  ClimateJobResponse, 
  ClimateJobDetailResponse, 
  JobListRequest, 
  JobDisplayInfo, 
  JobDetailDisplayInfo,
  Job,
  JobDetail,
  JobStatus
} from '../../../types/job';
import { ClimateApiError } from '../base/ClimateApiError';

/**
 * Job Service
 * 
 * Handles all job-related API operations including listing,
 * details retrieval, and data transformation.
 */
export class JobService {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/climate') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get jobs list with pagination and filtering
   */
  async getJobs(params: JobListRequest = {}): Promise<ClimateJobResponse> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.status) searchParams.append('status', params.status);
      if (params.job_type) searchParams.append('job_type', params.job_type);
      if (params.since) searchParams.append('since', params.since);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());

      const url = `${this.baseUrl}/jobs${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        throw await ClimateApiError.fromResponse(response);
      }

      const data = await response.json() as ClimateJobResponse;
      return data;

    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw ClimateApiError.fromNetworkError(error as Error);
    }
  }

  /**
   * Get job details by ID
   */
  async getJobById(jobId: string): Promise<ClimateJobDetailResponse> {
    try {
      const url = `${this.baseUrl}/jobs/${jobId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        throw await ClimateApiError.fromResponse(response);
      }

      const data = await response.json() as ClimateJobDetailResponse;
      return data;

    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw ClimateApiError.fromNetworkError(error as Error);
    }
  }

  /**
   * Normalize job status, setting default to "failed" if no status provided
   */
  private static normalizeJobStatus(status: string | null | undefined): JobStatus {
    const validStatuses = ['queued', 'running', 'completed', 'error', 'cancelled', 'paused', 'timeout', 'pending', 'failed', 'partial'];
    
    if (!status || !validStatuses.includes(status)) {
      return 'failed';
    }
    
    return status as JobStatus;
  }

  /**
   * Transform job data for display
   */
  static transformJobForDisplay(job: Job): JobDisplayInfo {
    return {
      id: job.job_id,
      type: job.job_type,
      status: this.normalizeJobStatus(job.status),
      progress: job.progress,
      message: job.message,
      createdAt: job.created_at,
      updatedAt: job.updated_at,
      startedAt: job.started_at,
      completedAt: job.completed_at,
      duration: this.calculateDuration(job.started_at, job.completed_at),
      timeoutSeconds: job.timeout_seconds,
      parentRequestId: job.parent_request_id
    };
  }

  /**
   * Transform job detail data for display
   */
  static transformJobDetailForDisplay(job: JobDetail): JobDetailDisplayInfo {
    const baseInfo = this.transformJobForDisplay(job);
    
    // Calculate error statistics
    const downloadResults = job.output?.download_results || [];
    const allErrors = downloadResults.reduce((acc: unknown[], result: { errors?: unknown[] }) => {
      return acc.concat(result.errors || []);
    }, []);
    
    const errorCount = allErrors.length;
    const successRate = (job.output as { success_rate?: number })?.success_rate || 
                       (downloadResults.length > 0 ? (downloadResults[0] as { success_rate?: number }).success_rate : undefined);

    return {
      ...baseInfo,
      input: job.input,
      output: job.output,
      errorDetails: job.error_details,
      controlFlags: {
        cancelRequested: job.control_flags?.cancel_requested || false,
        pauseRequested: job.control_flags?.pause_requested || false,
        timeoutAt: job.control_flags?.timeout_at || ''
      },
      hasErrors: errorCount > 0,
      errorCount,
      successRate,
      downloadResults
    };
  }

  /**
   * Calculate duration between two timestamps
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   */
  private static calculateDuration(startTime?: string, endTime?: string): string | undefined {
    if (!startTime) return undefined;
    
    // Parse ISO 8601 UTC timestamps - JavaScript Date automatically converts to local timezone
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    
    const diffMs = end.getTime() - start.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    
    if (diffSeconds < 60) {
      return `${diffSeconds}s`;
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Format timestamp for display
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   * Converts to user's local timezone automatically
   */
  static formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString();
  }

  /**
   * Get display status for job
   */
  static getDisplayStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'queued': 'Queued',
      'running': 'Running',
      'completed': 'Completed',
      'error': 'Error',
      'cancelled': 'Cancelled',
      'paused': 'Paused',
      'timeout': 'Timeout',
      'pending': 'Pending',
      'failed': 'Failed',
      'partial': 'Partial'
    };
    
    return statusMap[status] || status;
  }

  /**
   * Cancel a running job
   */
  async cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
    try {
      const url = `${this.baseUrl}/jobs/${jobId}/cancel`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        throw await ClimateApiError.fromResponse(response);
      }

      const data = await response.json() as { success: boolean; message: string };
      return data;

    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw ClimateApiError.fromNetworkError(error as Error);
    }
  }

  /**
   * Pause a running job
   */
  async pauseJob(jobId: string): Promise<{ success: boolean; message: string }> {
    try {
      const url = `${this.baseUrl}/jobs/${jobId}/pause`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        throw await ClimateApiError.fromResponse(response);
      }

      const data = await response.json() as { success: boolean; message: string };
      return data;

    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw ClimateApiError.fromNetworkError(error as Error);
    }
  }

  /**
   * Resume a paused job
   */
  async resumeJob(jobId: string): Promise<{ success: boolean; message: string }> {
    try {
      const url = `${this.baseUrl}/jobs/${jobId}/resume`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        throw await ClimateApiError.fromResponse(response);
      }

      const data = await response.json() as { success: boolean; message: string };
      return data;

    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw ClimateApiError.fromNetworkError(error as Error);
    }
  }
}

// Export singleton instance
export const jobService = new JobService(); 